import type { ReactElement } from 'react';
import { FormField, Select, TextInput } from '../form/index.js';
import { FilterPanel } from '../data/index.js';
import type {
  AttributeDefinition,
  CatalogModel,
  TaxonomyNode,
} from '../../lib/api/queries/catalog.js';

export interface FilterValues {
  taxonomy: string;
  manufacturer: string;
  model: string;
  from: string;
  to: string;
  /** Attribute code → chosen value. Only `eq` is offered publicly. */
  attributes: Record<string, string>;
}

export interface EquipmentFiltersProps {
  values: FilterValues;
  taxonomy: TaxonomyNode[];
  models: CatalogModel[];
  attributes: AttributeDefinition[];
  onChange: (next: FilterValues) => void;
  onClear: () => void;
  /** True while the catalogue that populates these controls is loading. */
  loading?: boolean;
}

/**
 * The filter panel is generated from the backend's published filter contract
 * (P-Q004 taxonomy, P-Q005 models, P-Q007 attributes) rather than from a list
 * this client keeps. That is what makes `422 validation.unknown_filter` fair:
 * a control only exists here if the registry says the filter exists.
 *
 * There is deliberately NO keyword box. P-Q011 accepts no free-text parameter
 * and rejects unknown keys outright, so a search field would either do nothing
 * or produce a validation error — either way it would be a lie about what the
 * product can find.
 */
export function EquipmentFilters({
  values,
  taxonomy,
  models,
  attributes,
  onChange,
  onClear,
  loading,
}: EquipmentFiltersProps): ReactElement {
  const set = (patch: Partial<FilterValues>): void => onChange({ ...values, ...patch });

  // Manufacturers are derived from the model catalogue, which already carries
  // the canonical name — there is no separate manufacturer endpoint.
  const manufacturers = [
    ...new Map(models.map((m) => [m.manufacturer_id, m.manufacturer])).entries(),
  ]
    .map(([id, name]) => ({ value: id, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const modelOptions = models
    .filter((m) => values.manufacturer === '' || m.manufacturer_id === values.manufacturer)
    .map((m) => ({ value: m.id, label: `${m.manufacturer} ${m.model_name}` }));

  // Only enum and boolean attributes can be offered as a control without
  // inventing a range widget the registry did not describe.
  const choosable = attributes.filter(
    (a) => a.is_filterable && (a.data_type === 'boolean' || (a.allowed_values?.length ?? 0) > 0),
  );

  const active =
    values.taxonomy !== '' ||
    values.manufacturer !== '' ||
    values.model !== '' ||
    values.from !== '' ||
    values.to !== '' ||
    Object.values(values.attributes).some((value) => value !== '');

  return (
    <FilterPanel label="Filter equipment" onClear={onClear} active={active}>
      <FormField label="Category">
        {(props) => (
          <Select
            {...props}
            disabled={loading === true}
            placeholder="Any category"
            value={values.taxonomy}
            onChange={(event) => set({ taxonomy: event.target.value })}
            options={taxonomy.map((node) => ({ value: node.code, label: node.name }))}
          />
        )}
      </FormField>

      <FormField label="Manufacturer">
        {(props) => (
          <Select
            {...props}
            disabled={loading === true}
            placeholder="Any manufacturer"
            value={values.manufacturer}
            onChange={(event) => set({ manufacturer: event.target.value, model: '' })}
            options={manufacturers}
          />
        )}
      </FormField>

      <FormField label="Model">
        {(props) => (
          <Select
            {...props}
            disabled={loading === true}
            placeholder="Any model"
            value={values.model}
            onChange={(event) => set({ model: event.target.value })}
            options={modelOptions}
          />
        )}
      </FormField>

      <fieldset className="filters__dates">
        <legend className="filters__legend">Dates needed</legend>
        <FormField label="From" hint="Both dates are needed to filter by availability.">
          {(props) => (
            <TextInput
              {...props}
              type="date"
              value={values.from}
              onChange={(event) => set({ from: event.target.value })}
            />
          )}
        </FormField>
        <FormField label="To">
          {(props) => (
            <TextInput
              {...props}
              type="date"
              value={values.to}
              onChange={(event) => set({ to: event.target.value })}
            />
          )}
        </FormField>
      </fieldset>

      {choosable.map((attribute) => (
        <FormField
          key={attribute.code}
          label={attribute.name}
          {...(attribute.unit_code === null ? {} : { hint: `Measured in ${attribute.unit_code}.` })}
        >
          {(props) => (
            <Select
              {...props}
              placeholder="Any"
              value={values.attributes[attribute.code] ?? ''}
              onChange={(event) =>
                set({
                  attributes: { ...values.attributes, [attribute.code]: event.target.value },
                })
              }
              options={
                attribute.data_type === 'boolean'
                  ? [
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]
                  : (attribute.allowed_values ?? []).map((code) => ({
                      value: code,
                      label: code.replace(/_/g, ' '),
                    }))
              }
            />
          )}
        </FormField>
      ))}
    </FilterPanel>
  );
}
